// MUI typography configuration — matched to the finalized Deview design.
// Compact, data-dense scale. Numbers/dates/tickers use DM Mono with
// tabular figures via theme.tokens.monoFontFamily (see tokens.ts).
import { TypographyVariantsOptions } from '@mui/material/styles';

// Sizes are expressed in rem so they scale with the user's browser font-size /
// zoom (accessibility). The design specifies px, so `rem(px)` keeps that intent
// readable while emitting rem, assuming MUI's default 16px root.
const ROOT_FONT_SIZE = 16;
const rem = (px: number): string => `${px / ROOT_FONT_SIZE}rem`;

const typography: TypographyVariantsOptions = {
  fontFamily: [
    '"DM Sans"',
    'system-ui',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  fontWeightMedium: 500,

  // Hero banner title
  h1: {
    fontWeight: 700,
    fontSize: rem(24),
    lineHeight: 1.2,
    letterSpacing: '-0.5px',
  },
  // Page title
  h2: {
    fontWeight: 700,
    fontSize: rem(20),
    lineHeight: 1.2,
    letterSpacing: '-0.4px',
  },
  // Reserve
  h3: {
    fontWeight: 700,
    fontSize: rem(16),
    lineHeight: 1.25,
    letterSpacing: '-0.3px',
  },
  // Toolbar / drawer titles, brand
  h4: {
    fontWeight: 700,
    fontSize: rem(14),
    lineHeight: 1.3,
    letterSpacing: '-0.3px',
  },
  // Panel titles (e.g. "Node palette")
  h5: {
    fontWeight: 700,
    fontSize: rem(12.5),
    lineHeight: 1.3,
  },
  h6: {
    fontWeight: 600,
    fontSize: rem(12),
    lineHeight: 1.3,
  },
  // Page subtitle / descriptions
  subtitle1: {
    fontWeight: 400,
    fontSize: rem(13),
    lineHeight: 1.55,
  },
  // Table primary cell (bot name)
  subtitle2: {
    fontWeight: 600,
    fontSize: rem(12.5),
    lineHeight: 1.4,
  },
  // Default body, nav items
  body1: {
    fontSize: rem(13),
    lineHeight: 1.55,
  },
  // Table cells, filters
  body2: {
    fontSize: rem(12.5),
    lineHeight: 1.5,
  },
  // Primary buttons (12px in dense toolbars)
  button: {
    fontWeight: 600,
    fontSize: rem(13),
    textTransform: 'none',
  },
  // Cell sub-text, helper text
  caption: {
    fontSize: rem(11),
    lineHeight: 1.45,
  },
  // Table headers, card labels
  overline: {
    fontSize: rem(10.5),
    fontWeight: 600,
    letterSpacing: '0.5px',
    lineHeight: 1.6,
    textTransform: 'uppercase',
  },
};

/**
 * Display styles with no MUI variant slot — apply via sx.
 * All render in DM Mono (theme.tokens.monoFontFamily) with tabular-nums.
 */
export const metricStyles = {
  /** Stat card numbers */
  lg: {
    fontSize: rem(30),
    fontWeight: 700,
    letterSpacing: '-1px',
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  /** Summary-bar numbers */
  md: {
    fontSize: rem(22),
    fontWeight: 700,
    letterSpacing: '-0.5px',
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums',
  },
  /** Hero stat chips */
  sm: {
    fontSize: rem(20),
    fontWeight: 700,
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums',
  },
} as const;

export default typography;
